// =============================================
// MODAL DE CIERRE DE PERÍODO
// Muestra balance calculado vs real y permite cerrar
// =============================================

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Typography,
  Box,
  Divider,
  Paper,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import periodService from '../../services/periodService';

const ClosePeriodModal = ({ open, onClose, period, onSuccess }) => {
  // PERÍODOS: Estados
  const [loading, setLoading] = useState(false);
  const [loadingStats, setLoadingStats] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  
  // PERÍODOS: Estado del formulario
  const [formData, setFormData] = useState({
    actual_end_balance: '',
    closing_notes: '',
  });

  // PERÍODOS: Cargar estadísticas al abrir
  useEffect(() => {
    if (open && period) {
      loadStats();
    }
  }, [open, period]);

  // PERÍODOS: Cargar estadísticas
  const loadStats = async () => {
    try {
      setLoadingStats(true);
      const response = await periodService.getStats(period.id);
      setStats(response.stats);
      
      // Pre-llenar con el balance calculado
      setFormData({
        actual_end_balance: response.stats.balance.calculated.toFixed(2),
        closing_notes: '',
      });
      
      setError('');
    } catch (err) {
      setError('Error al cargar estadísticas');
      console.error(err);
    } finally {
      setLoadingStats(false);
    }
  };

  // PERÍODOS: Manejar cambios
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    setError('');
  };

  // PERÍODOS: Calcular diferencia
  const getDifference = () => {
    if (!stats || !formData.actual_end_balance) return 0;
    return parseFloat(formData.actual_end_balance) - stats.balance.calculated;
  };

  // PERÍODOS: Validar y cerrar
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validar saldo real
      if (!formData.actual_end_balance || formData.actual_end_balance === '') {
        setError('El saldo real al cierre es requerido');
        setLoading(false);
        return;
      }

      const difference = getDifference();
      
      // Si hay diferencia significativa, requerir notas
      if (Math.abs(difference) > 0.01 && !formData.closing_notes.trim()) {
        setError('Debes justificar la diferencia entre el saldo calculado y el real');
        setLoading(false);
        return;
      }

      // Cerrar período
      const closeData = {
        actual_end_balance: parseFloat(formData.actual_end_balance),
        closing_notes: formData.closing_notes.trim() || null,
      };

      await periodService.close(period.id, closeData);
      
      onSuccess();
      onClose();

    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Error al cerrar período';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // PERÍODOS: Formatear moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PA', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Cerrar Período {period?.year}
      </DialogTitle>

      <DialogContent>
        {/* PERÍODOS: Mostrar errores */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loadingStats ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : stats ? (
          <>
            {/* PERÍODOS: Resumen del período */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="h6" gutterBottom>
                Resumen del Período
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Saldo Inicial:
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(stats.balance.initial)}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total Pagos:
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    +{formatCurrency(stats.payments.total)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({stats.payments.count} pagos)
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total Gastos:
                  </Typography>
                  <Typography variant="h6" color="error.main">
                    -{formatCurrency(stats.expenses.total)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({stats.expenses.count} gastos)
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Balance Calculado:
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    {formatCurrency(stats.balance.calculated)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <Divider sx={{ my: 2 }} />

            {/* PERÍODOS: Formulario de cierre */}
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                {/* PERÍODOS: Saldo real al cierre */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Saldo Real al Cierre"
                    name="actual_end_balance"
                    type="number"
                    value={formData.actual_end_balance}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    inputProps={{ step: '0.01' }}
                    helperText="Ingresa el saldo real que quedó en caja/banco"
                  />
                </Grid>

                {/* PERÍODOS: Mostrar diferencia */}
                {formData.actual_end_balance && (
                  <Grid item xs={12}>
                    <Paper 
                      sx={{ 
                        p: 2, 
                        bgcolor: Math.abs(getDifference()) < 0.01 ? 'success.light' : 'warning.light'
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        {Math.abs(getDifference()) < 0.01 ? (
                          <>
                            <CheckIcon color="success" />
                            <Typography variant="body1">
                              <strong>Perfecto:</strong> El saldo calculado coincide con el real
                            </Typography>
                          </>
                        ) : (
                          <>
                            <WarningIcon color="warning" />
                            <Box>
                              <Typography variant="body1">
                                <strong>Diferencia detectada:</strong> {formatCurrency(Math.abs(getDifference()))}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {getDifference() > 0 ? 'Hay más dinero del esperado' : 'Falta dinero'}
                              </Typography>
                            </Box>
                          </>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                )}

                {/* PERÍODOS: Notas de cierre */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notas de Cierre"
                    name="closing_notes"
                    value={formData.closing_notes}
                    onChange={handleChange}
                    disabled={loading}
                    multiline
                    rows={4}
                    required={Math.abs(getDifference()) > 0.01}
                    helperText={
                      Math.abs(getDifference()) > 0.01 
                        ? "⚠️ Debes explicar la diferencia entre el saldo calculado y el real"
                        : "Notas opcionales sobre el cierre del período"
                    }
                  />
                </Grid>
              </Grid>

              {/* PERÍODOS: Advertencia */}
              <Alert severity="warning" sx={{ mt: 2 }}>
                <strong>Importante:</strong> Al cerrar el período, se guardará el saldo real ingresado.
                Asegúrate de que los datos sean correctos.
              </Alert>
            </form>
          </>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} size="large">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="error"
          disabled={loading || loadingStats}
          size="large"
        >
          {loading ? <CircularProgress size={24} /> : 'Cerrar Período'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClosePeriodModal;
